import { NetworkLocation } from '../TypeDefinitions';
import { Logger } from '../Logger';
import { logConstants } from '../../utils/Constants';
import uuid from '../../utils/uuid';
import DSAuth from '../dsAuth/DSAuth';
import AuthMan from '../AuthMan';

export interface NetworkLocationInfo {
	beacon: string;
	location: NetworkLocation;
}

enum BeaconRequestStatus {
	Waiting = 'Waiting',
	InProgress = 'InProgress',
	Completed = 'Completed',
}

export class QueuedBeaconRequest {
	readonly id: string;
	readonly referenceUri: string;
	resolve: Function;
	reject: Function;
	status: BeaconRequestStatus;

	constructor(id: string, referenceUri: string, status: BeaconRequestStatus) {
		this.id = id;
		this.referenceUri = referenceUri;
		this.status = status;
	}
}

/**
 * Class to handle the fetching of network location for stores
 * Maintains a cache of network locations for beacons, and handles the queuing of requests when multiple requests are made for the same referenceUri
 * networkLocationCache - Map to store the network location for each referenceUri
 * beaconRequestQueue - Queue to store the requests for the same referenceUri when multiple requests are made for the same referenceUri
 */
export default class NetworkLocationHandler {
	private static instance: NetworkLocationHandler;
	private networkLocationCache: Map<string, NetworkLocationInfo> = new Map();
	private beaconRequestQueue: QueuedBeaconRequest[] = [];
	private readonly NUM_RETRIES = 10;

	public static getInstance(): NetworkLocationHandler {
		if (!NetworkLocationHandler.instance) {
			NetworkLocationHandler.instance = new NetworkLocationHandler();
		}
		return NetworkLocationHandler.instance;
	}

	/**
	 * Invalidate the network location cache and reevaluate the network location for all beacons, this is called when the network changes
	 * @param {string} referenceUri - The referenceUri for which store, beacon evaluation is done on priority
	 * @returns {Promise<void>}
	 */
	public async ReevaluateBeaconsOnNetworkChange(referenceUri?: string): Promise<void> {
		Logger.getInstance().write(
			logConstants.INFO,
			'Reevaluating all beacons on network change'
		);

		// Clear the cache and set the location to None for all beacons
		this.networkLocationCache.forEach((value, key) => {
			const networkLocationInfo = {
				beacon: value.beacon,
				location: NetworkLocation.None,
			};
			this.networkLocationCache.set(key, networkLocationInfo);
		});

		// Get the network location for the referenceUri if given
		const currentStoreBeacon = this.networkLocationCache.get(referenceUri)?.beacon;
		if (currentStoreBeacon !== undefined) {
			const currentStoreLocation = await this.GetLocation(currentStoreBeacon);
			this.networkLocationCache.set(referenceUri, {
				beacon: currentStoreBeacon,
				location: currentStoreLocation,
			});
		}

		// Dispatch the network location evaluation for all beacons except the referenceUri
		const locationPromises = Array.from(this.networkLocationCache.entries())
			.filter(([key]) => key != referenceUri)
			.map(async ([key, value]) => {
				const networkLocation = this.GetLocation(value.beacon);
				return {
					key,
					networkLocationInfo: { beacon: value.beacon, location: networkLocation },
				};
			});

		const resolvedLocations = await Promise.allSettled(locationPromises);

		resolvedLocations.forEach((result) => {
			if (result.status === 'fulfilled') {
				const { key, networkLocationInfo } = result.value;
				this.networkLocationCache.set(key, networkLocationInfo);
			} else {
				const { networkLocationInfo, reason } = result.reason;
				Logger.getInstance().write(
					logConstants.ERROR,
					`Error getting location for beacon ${networkLocationInfo.beacon}: ${reason}`
				);
			}
		});
	}

	/**
	 * Get the network location given the beacon
	 * @param {string} beacon - The beacon to be used to determine the network location
	 * @returns {Promise<NetworkLocation>} - The network location
	 */
	public async GetNetworkLocationForURL(beacon: string): Promise<NetworkLocation> {
		return await this.GetLocation(beacon);
	}

	/**
	 * API to get the network location given the beacon and referenceUri,
	 * this function will also handle the queuing of requests when multiple requests are made for the same referenceUri
	 * This function returns the cached network location if getCachedLocation is set to true
	 * @param {string} referenceUri - The referenceUri to be used to determine the network location
	 * @param {string} beacon - The beacon to be used to determine the network location
	 * @returns {Promise<NetworkLocation>} - The network location
	 */
	public async GetNetworkLocationForStore(
		referenceUri: string,
		beacon?: string
	): Promise<NetworkLocation> {
		try {
			Logger.getInstance().write(
				logConstants.INFO,
				'Getting location for url: ' + beacon + ' with referenceUri: ' + referenceUri
			);

			// Getting cached network location
			const cachedNetworkLocation = this.GetNetworkLocationFromCache(referenceUri);
			if (
				cachedNetworkLocation == NetworkLocation.Inside ||
				cachedNetworkLocation == NetworkLocation.Outside
			) {
				return cachedNetworkLocation;
			}

			// Trying to get the beacon from the store configuration if not provided
			if (!beacon) {
				const storeConfig = AuthMan.getInstance().getStoreConfiguration();
				if (storeConfig[referenceUri] && storeConfig[referenceUri].beacon) {
					beacon = storeConfig[referenceUri].beacon;
				} else {
					Logger.getInstance().write(
						logConstants.INFO,
						`Beacon not found for the store with referenceUri: ${referenceUri}`
					);
					throw new Error(
						'Beacon not found for the store with referenceUri: ' + referenceUri
					);
				}
			}

			const id = uuid();
			const requestPresent = this.beaconRequestQueue.some(
				(req) =>
					req.referenceUri === referenceUri &&
					(req.status === BeaconRequestStatus.InProgress ||
						req.status === BeaconRequestStatus.Completed)
			);

			/**
			 * If there is already a request in progress or waiting for the same referenceUri, then queue the request and wait for the previous request to complete
			 * No need to trigger the network location evaluation again for the new request - so await on the previous request
			 */
			if (requestPresent) {
				const queuedBeaconRequest = new QueuedBeaconRequest(
					id,
					referenceUri,
					BeaconRequestStatus.Waiting
				);
				this.beaconRequestQueue.push(queuedBeaconRequest);

				await new Promise((resolve: Function, reject: Function) => {
					queuedBeaconRequest.resolve = resolve;
					queuedBeaconRequest.reject = reject;
				});
			} else {
				const queuedBeaconRequest = new QueuedBeaconRequest(
					id,
					referenceUri,
					BeaconRequestStatus.InProgress
				);
				this.beaconRequestQueue.push(queuedBeaconRequest);

				this.networkLocationCache.set(referenceUri, {
					beacon: beacon,
					location: NetworkLocation.Deciding,
				});
				const networkLocation = await this.GetLocation(beacon);
				this.ProcessQueuedRequests(networkLocation, referenceUri);
			}

			const index = this.beaconRequestQueue.findIndex((req) => req.id === id);
			this.beaconRequestQueue.splice(index, 1);
			return this.networkLocationCache.get(referenceUri)?.location;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error while getting network location: ' + e.message
			);
			return NetworkLocation.None;
		}
	}

	/**
	 * API to get the network location from the cache given referenceUri
	 * @param referenceUri - The referenceUri to be used to determine the network location
	 * @returns {NetworkLocation} - The network location
	 */
	public GetNetworkLocationFromCache(referenceUri: string): NetworkLocation {
		return this.networkLocationCache.get(referenceUri)?.location ?? NetworkLocation.None;
	}

	/**
	 * Function to get the network location given the beacon, this will make a HEAD request to the beacon and check the response status code to determine the network location
	 * @param beacon - The beacon to be used to determine the network location
	 * @returns	{Promise<NetworkLocation>} - The network location
	 */
	private async GetLocation(beacon: string): Promise<NetworkLocation> {
		if (!beacon) {
			return NetworkLocation.Outside;
		}

		try {
			const beaconUrl = new URL(beacon);
			let networkLocation = NetworkLocation.Outside;

			for (let i = 0; i < this.NUM_RETRIES; i++) {
				const response = await DSAuth.getInstance().fetch(
					'HEAD',
					beacon,
					{
						Host: beaconUrl.hostname,
						Connection: 'Keep-Alive',
					},
					'',
					'',
					false,
					''
				);

				if (
					(response.statusCode >= 200 && response.statusCode <= 399) ||
					(response.statusCode >= 401 && response.statusCode <= 403)
				) {
					networkLocation = NetworkLocation.Inside;
					break;
				}
			}

			return networkLocation;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error while making beacon request: ' + e.message
			);
			return NetworkLocation.None;
		}
	}

	/**
	 * Function to process the queued requests for the same referenceUri, resolve the promises of other requests in the queue that are waiting for the network location to be set
	 * @param networkLocation - The network location to be set for the referenceUri
	 * @param referenceUri - The referenceUri for which the network location is to be set
	 */
	private async ProcessQueuedRequests(
		networkLocation: NetworkLocation,
		referenceUri: string
	): Promise<void> {
		this.networkLocationCache.set(referenceUri, {
			...this.networkLocationCache.get(referenceUri),
			location: networkLocation,
		});

		const queuedRequests = this.beaconRequestQueue.filter(
			(req) => req.referenceUri === referenceUri
		);

		queuedRequests.forEach((queuedRequest) => {
			queuedRequest.status = BeaconRequestStatus.Completed;
			if (queuedRequest.resolve) {
				queuedRequest.resolve();
			}
		});
	}
}
